import React, { useState, useRef, useEffect } from 'react';
import Slider from 'react-slick';
import { twMerge } from 'tailwind-merge';
import useBreakpointView from 'utils/useBreakpointView';
import leftArrow from 'images/left-arrow-circle.svg';
import richTextOptions from './richTextOptions';
import iconLinkArrow from '../images/link-arrow.svg';
import rightArrow from 'images/right-arrow-circle.svg';
import RichText from 'components/RichText';
import BackgroundImage from 'components/Core/BackgroundImage';
import WebsiteImage from 'components/WebsiteImage';
import WistiaVideo from 'components/Core/WistiaVideo';
import StyledSectionLayout from './styles';
import HyperLink from 'components/Core/HyperLink';

const InPageFeaturesV7 = ({ title, className, description, inPageNavigationItems }) => {
  const [selectedNavigation, setSelectedNavigation] = useState(0);
  const [nav1, setNav1] = useState(null);
  const [nav2, setNav2] = useState(null);
  const [showNavArrows, setShowNavArrows] = useState(false);
  const [isSliding, setIsSliding] = useState(false);
  const navSliderContainerRef = useRef(null);
  const useMobileStyle = useBreakpointView([0, 1350]);
  const hideArrows = !showNavArrows && !useMobileStyle;

  useEffect(() => {
    const checkOverflow = () => {
      const slickListElement = navSliderContainerRef.current?.querySelector('.slick-list');

      if (slickListElement) {
        const slickTrackElement = slickListElement.querySelector('.slick-track');

        const totalWidth = Array.from(slickTrackElement.children)
          .filter((child) => child.tagName === 'DIV' && !child.classList.contains('slick-cloned'))
          .reduce((acc, child) => acc + child.offsetWidth, 0);

        const isOverflowing = totalWidth > slickListElement.clientWidth;

        setShowNavArrows(isOverflowing);
      }
    };

    checkOverflow();

    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [inPageNavigationItems, useMobileStyle]);

  const FeatureCopy = ({ fullWidthFeaturedContent, item, includesTitle }) => {
    const { description, link, title } = item || {};

    const includesCopy = description || link || title;

    if (!includesCopy) {
      return null;
    }

    const HeadingTag = includesTitle ? 'h3' : 'h2';
    const headingClassName = '!mt-0 !mb-[20px] md-max:!mb-[10px] !text-left';

    return (
      <div
        className={twMerge(
          'flex-1 max-w-[797px] [&_p]:mt-[15px] [&_p]:mb-0 [&_h2]:!mb-0 [&_h3]:!mb-0',
          fullWidthFeaturedContent ? 'mt-[30px] md:mt-0 md:mb-[40px]' : 'block',
        )}
      >
        {title && <HeadingTag className={headingClassName}>{title}</HeadingTag>}
        {description && <RichText content={description} options={richTextOptions} />}
        {link && (
          <HyperLink
            href={item.link}
            className="flex items-baseline mt-[40px] md-max:mt-[20px] w-fit font-heading text-titan-blue-3 font-bold"
          >
            Learn More
            <img className="!ml-[10px]" src={iconLinkArrow} alt="link" />
          </HyperLink>
        )}
      </div>
    );
  };

  return (
    <StyledSectionLayout>
      <div className={className}>
        <div className="mb-[40px]">
          <h2 className="!text-left mb-0">{title}</h2>
          <p className="font-heading text-[20px] md-max:text-[17px] !text-left text-darkest mb-0 mt-[15px]">
            {description?.description}
          </p>
        </div>
        <div
          ref={navSliderContainerRef}
          className={twMerge(
            'in-page-features--navigation-content w-full',
            selectedNavigation !== 0 && 'fade-prev',
          )}
        >
          <Slider
            asNavFor={nav2}
            ref={(slider) => setNav1(slider)}
            variableWidth
            swipeToSlide
            focusOnSelect
            arrows={showNavArrows && !useMobileStyle}
            infinite={useMobileStyle}
            centerMode={useMobileStyle}
            autoplay={false}
            autoplaySpeed={5000}
            speed={500}
            nextArrow={<img src={rightArrow} alt="next-arrow" />}
            prevArrow={<img src={leftArrow} alt="prev-arrow" />}
            afterChange={(index) => {
              setSelectedNavigation(index);
              setIsSliding(false);
            }}
            beforeChange={() => setIsSliding(true)}
            adaptiveHeight={true}
            className={hideArrows ? 'static-slider' : ''}
          >
            {inPageNavigationItems?.map((item, index) => (
              <React.Fragment key={`in-page-nav-${index}`}>
                <div
                  className={twMerge(
                    `relative mr-[5px] font-heading sm:text-desktop-h5 font-normal text-grey-6 border-0 border-b-2 border-solid border-transparent py-[10px] whitespace-nowrap`,
                    !isSliding && 'hover:font-bold',
                    selectedNavigation === index &&
                      '!text-titan-blue-3 !font-bold !border-titan-blue-3',
                    isSliding ? 'cursor-arrow' : 'cursor-pointer',
                  )}
                  onClick={() => {
                    if (!isSliding) {
                      nav1.slickGoTo(index);
                    }
                  }}
                >
                  <span className="font-bold invisible">{item.navigationTitle || item.title}</span>
                  <span className="absolute left-0">{item.navigationTitle || item.title}</span>
                </div>
              </React.Fragment>
            ))}
          </Slider>
        </div>
        <div className="in-page-features--main-content mt-8 md:mt-14">
          <Slider
            asNavFor={nav1}
            ref={(slider) => setNav2(slider)}
            swipeToSlide
            focusOnSelect
            arrows={false}
            autoplay={false}
            autoplaySpeed={5000}
            slidesToShow={1}
            infinite={useMobileStyle}
            speed={500}
            adaptiveHeight={true}
          >
            {inPageNavigationItems?.map((item, index) => {
              const { featuredImage, featuredVideo, fullWidthFeaturedContent } = item || {};

              return (
                <div
                  className={twMerge(
                    fullWidthFeaturedContent && '!flex !flex-col-reverse md:!flex-col',
                  )}
                  key={`in-page-nav-main-${index}`}
                >
                  {fullWidthFeaturedContent && (
                    <FeatureCopy
                      fullWidthFeaturedContent={fullWidthFeaturedContent}
                      item={item}
                      includesTitle={title}
                    />
                  )}
                  <div className="flex md-max:flex-col-reverse items-center md-max:items-start justify-center gap-[60px] md-max:gap-[30px]">
                    {!fullWidthFeaturedContent && (
                      <FeatureCopy
                        fullWidthFeaturedContent={fullWidthFeaturedContent}
                        item={item}
                        includesTitle={title}
                      />
                    )}
                    <div className="flex-1 relative min-h-[370px] md-max:min-h-[180px] w-full">
                      {featuredImage && fullWidthFeaturedContent && (
                        <WebsiteImage {...featuredImage} className="[&_img]:!object-contain" />
                      )}
                      {featuredImage && !fullWidthFeaturedContent && (
                        <BackgroundImage image={featuredImage} objectFit="contain" />
                      )}
                      {featuredVideo && <WistiaVideo {...featuredVideo} />}
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
    </StyledSectionLayout>
  );
};

export default InPageFeaturesV7;
